import React, { useRef, useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';

const SignatureCanvas = ({ onSave, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;

    const ctx = canvas?.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef?.current;
    const rect = canvas?.getBoundingClientRect();
    const ctx = canvas?.getContext('2d');

    setIsDrawing(true);
    setHasSignature(true);

    const x = e?.clientX ? e?.clientX - rect?.left : e?.touches?.[0]?.clientX - rect?.left;
    const y = e?.clientY ? e?.clientY - rect?.top : e?.touches?.[0]?.clientY - rect?.top;

    ctx?.beginPath();
    ctx?.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef?.current;
    const rect = canvas?.getBoundingClientRect();
    const ctx = canvas?.getContext('2d');

    const x = e?.clientX ? e?.clientX - rect?.left : e?.touches?.[0]?.clientX - rect?.left;
    const y = e?.clientY ? e?.clientY - rect?.top : e?.touches?.[0]?.clientY - rect?.top;

    ctx?.lineTo(x, y);
    ctx?.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
    setHasSignature(false);
    onClear?.();
  };

  const saveSignature = () => {
    const canvas = canvasRef?.current;
    const signatureData = canvas?.toDataURL('image/png');
    onSave?.(signatureData);
  };

  return (
    <div className="space-y-3">
      <div className="border-2 border-dashed border-border rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          className="w-full cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconName="Eraser"
          onClick={clearCanvas}
          disabled={!hasSignature}
        >
          Limpiar
        </Button>
        <Button
          type="button"
          variant="default"
          size="sm"
          iconName="Check"
          onClick={saveSignature}
          disabled={!hasSignature}
        >
          Guardar Firma
        </Button>
      </div>
    </div>
  );
};

const ApprovalRequestForm = ({ 
  workOrderId,
  quotationTotal,
  clientEmail,
  clientName,
  onSubmit,
  onCancel,
  managers = [],
  isSubmitting = false
}) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    managerId: '',
    requestNotes: '',
    signatureData: null
  });
  const [errors, setErrors] = useState({});
  const [signatureSaved, setSignatureSaved] = useState(false);

  const managerOptions = managers?.map(m => ({
    value: m?.id,
    label: m?.fullName || m?.email,
    description: m?.role
  }));

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSignatureSave = (signatureData) => {
    setFormData(prev => ({ ...prev, signatureData }));
    setSignatureSaved(true);
    setErrors(prev => ({ ...prev, signatureData: '' }));
  };

  const handleSignatureClear = () => {
    setFormData(prev => ({ ...prev, signatureData: null }));
    setSignatureSaved(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.managerId) {
      newErrors.managerId = 'Seleccione un gerente para revisar';
    }

    if (!formData?.requestNotes?.trim()) {
      newErrors.requestNotes = 'Ingrese notas de solicitud';
    }

    if (!formData?.signatureData) {
      newErrors.signatureData = 'Capture su firma digital';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSubmit?.({
        workOrderId,
        quotationTotal,
        requestedBy: userProfile?.id,
        managerId: formData?.managerId,
        requestNotes: formData?.requestNotes,
        signatureData: formData?.signatureData,
        clientEmail,
        clientName,
        status: 'pending'
      });
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-primary/10">
          <Icon name="FileCheck" size={24} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Solicitud de Aprobación
          </h2>
          <p className="text-sm caption text-muted-foreground">
            Enviar cotización para revisión gerencial
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm caption text-muted-foreground">Orden de Trabajo:</span>
            <span className="text-sm font-body font-semibold text-foreground">{workOrderId}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm caption text-muted-foreground">Monto Total:</span>
            <span className="text-lg font-heading font-bold text-primary data-text">
              ${parseFloat(quotationTotal || 0)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm caption text-muted-foreground">Cliente:</span>
            <span className="text-sm font-body font-semibold text-foreground">{clientName}</span>
          </div>
        </div>

        <Select
          label="Gerente Revisor"
          required
          options={managerOptions}
          value={formData?.managerId}
          onChange={(value) => handleChange('managerId', value)}
          error={errors?.managerId}
          placeholder="Seleccione gerente"
          searchable
        />

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Notas de Solicitud
            <span className="text-destructive ml-1">*</span>
          </label>
          <textarea
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[100px]"
            placeholder="Ej: Cliente solicita aprobación urgente para iniciar trabajos mañana..."
            value={formData?.requestNotes}
            onChange={(e) => handleChange('requestNotes', e?.target?.value)}
          />
          {errors?.requestNotes && (
            <p className="text-sm text-destructive">{errors?.requestNotes}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Firma Digital
            <span className="text-destructive ml-1">*</span>
          </label>
          <p className="text-xs caption text-muted-foreground mb-3">
            Dibuje su firma en el recuadro usando el mouse o pantalla táctil
          </p>
          <SignatureCanvas
            onSave={handleSignatureSave}
            onClear={handleSignatureClear}
          />
          {signatureSaved && (
            <div className="flex items-center gap-2 text-success text-sm">
              <Icon name="CheckCircle" size={16} />
              <span>Firma guardada correctamente</span>
            </div>
          )}
          {errors?.signatureData && (
            <p className="text-sm text-destructive">{errors?.signatureData}</p>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-heading font-semibold text-blue-900 mb-1">
                Proceso de Aprobación
              </h4>
              <ul className="space-y-1 text-xs caption text-blue-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>El gerente seleccionado recibirá una notificación para revisar la cotización</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>Una vez aprobada, se enviará automáticamente un correo al cliente</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>Podrá dar seguimiento al estado de la solicitud en tiempo real</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-border">
          <Button
            type="submit"
            variant="default"
            iconName="Send"
            iconPosition="left"
            loading={isSubmitting}
            disabled={isSubmitting}
            fullWidth
          >
            Enviar Solicitud de Aprobación
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ApprovalRequestForm;